import { useCallback, useMemo, useState } from 'react'
import { Alert, Box, Button, Center, Divider, Flex, LoadingOverlay, ScrollArea, Stack, Text } from '@mantine/core'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { TbAlertTriangle, TbDownload } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { getDetectionComparisonOptions } from '@/api/main'
import type { DetectionComparisonEntity } from '@/api/main/types.gen'
import PageShell from '@/components/Common/PageShell'
import type { TableColumn } from '@/components/Table/types'
import { useCsvDownload } from '@/hooks/useCsvDownload'
import useDebounce from '@/hooks/useDebounceSearch'
import { usePageTitle } from '@/hooks/usePageTitle'
import { CITY_NAME } from '@/meta'
import { errorMessage } from '@/utils/other'
import {
  buildExportRows,
  buildLayers,
  CATEGORY_KEYS,
  countByCategory,
  countByDrive,
  DEFAULT_MATCHING_PARAMS,
  entityBounds,
  filterEntities,
  responseBounds,
  type CategoryKey,
  type ExportRow,
  type MatchingParams,
} from './comparison'
import { DetectionComparisonMap, type MapFocus } from './DetectionComparisonMap'
import { DriveSelectors } from './DriveSelectors'
import { EntityDetail } from './EntityDetail'
import { EntityList } from './EntityList'
import { DisplayFilters, MatchingParamsControls } from './MatchingControls'

const SIDEBAR_WIDTH = 340
const PARAMS_DEBOUNCE_MS = 400
const PAGE_HEIGHT = 'calc(100vh - 180px)'

type Props = {
  firstDriveId: number | undefined
  secondDriveId: number | undefined
  onDrivesChange: (firstDriveId: number | undefined, secondDriveId: number | undefined) => void
}

const toggle = <T,>(set: ReadonlySet<T>, value: T, present: boolean): Set<T> => {
  const next = new Set(set)
  if (present) next.add(value)
  else next.delete(value)
  return next
}

const EXPORT_COLUMN_KEYS: Array<[keyof ExportRow, string]> = [
  ['category', 'category'],
  ['first_detection_id', 'firstDetectionId'],
  ['first_plate', 'firstPlate'],
  ['first_observed_at', 'firstObservedAt'],
  ['first_latitude', 'firstLatitude'],
  ['first_longitude', 'firstLongitude'],
  ['second_detection_id', 'secondDetectionId'],
  ['second_plate', 'secondPlate'],
  ['second_observed_at', 'secondObservedAt'],
  ['second_latitude', 'secondLatitude'],
  ['second_longitude', 'secondLongitude'],
  ['edit_distance', 'editDistance'],
  ['separation_m', 'separationM'],
]

const overlayAlertStyle = { position: 'absolute', zIndex: 6, left: 0, right: 0 } as const

export function DetectionComparisonPage({ firstDriveId, secondDriveId, onDrivesChange }: Props) {
  const { t } = useTranslation('detectionComparison')
  usePageTitle(`${t('title')} | ${CITY_NAME}`)

  const [params, setParams] = useState<MatchingParams>(DEFAULT_MATCHING_PARAMS)
  const debouncedParams = useDebounce(params, PARAMS_DEBOUNCE_MS)
  const [hiddenCategories, setHiddenCategories] = useState<ReadonlySet<CategoryKey>>(new Set())
  const [hiddenDrives, setHiddenDrives] = useState<ReadonlySet<number>>(new Set())
  const [search, setSearch] = useState('')
  const [showPairLines, setShowPairLines] = useState(true)
  const [showRejectedLinks, setShowRejectedLinks] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [focus, setFocus] = useState<MapFocus | null>(null)

  const hasPair = firstDriveId !== undefined && secondDriveId !== undefined && firstDriveId !== secondDriveId

  const comparison = useQuery({
    ...getDetectionComparisonOptions({
      path: { first_drive_id: firstDriveId ?? 0, second_drive_id: secondDriveId ?? 0 },
      query: {
        max_distance_m: debouncedParams.maxDistanceM,
        max_edit_distance: debouncedParams.maxEditDistance,
        seen_once_suppression_radius_m: debouncedParams.suppressionRadiusM,
      },
    }),
    enabled: hasPair,
    placeholderData: keepPreviousData,
  })
  // Previous data is kept only across parameter changes; a response for another drive pair
  // would be classified against the wrong drive ids.
  const data =
    hasPair && comparison.data?.first_drive_id === firstDriveId && comparison.data.second_drive_id === secondDriveId
      ? comparison.data
      : undefined
  const entities = useMemo(() => data?.entities ?? [], [data])

  const visibleCategories = useMemo(
    () => new Set(CATEGORY_KEYS.filter(key => !hiddenCategories.has(key))),
    [hiddenCategories]
  )
  const visibleDrives = useMemo(
    () =>
      new Set([firstDriveId, secondDriveId].filter((id): id is number => id !== undefined && !hiddenDrives.has(id))),
    [firstDriveId, secondDriveId, hiddenDrives]
  )

  const filtered = useMemo(
    () =>
      firstDriveId === undefined
        ? []
        : filterEntities(entities, { categories: visibleCategories, drives: visibleDrives, search }, firstDriveId),
    [entities, visibleCategories, visibleDrives, search, firstDriveId]
  )
  const countable = useMemo(
    () =>
      firstDriveId === undefined
        ? []
        : filterEntities(entities, { categories: new Set(CATEGORY_KEYS), drives: visibleDrives, search }, firstDriveId),
    [entities, visibleDrives, search, firstDriveId]
  )
  const categoryCounts = useMemo(() => countByCategory(countable, firstDriveId ?? 0), [countable, firstDriveId])
  const driveCounts = useMemo(() => countByDrive(entities), [entities])

  const layerOptions = useMemo(
    () => ({ firstDriveId: firstDriveId ?? 0, drives: visibleDrives, showPairLines, showRejectedLinks }),
    [firstDriveId, visibleDrives, showPairLines, showRejectedLinks]
  )
  const layers = useMemo(() => buildLayers(filtered, layerOptions), [filtered, layerOptions])
  const selectedEntity = useMemo(
    () => filtered.find(entity => entity.id === selectedId) ?? null,
    [filtered, selectedId]
  )
  const selectionLayers = useMemo(
    () => (selectedEntity ? buildLayers([selectedEntity], layerOptions) : null),
    [selectedEntity, layerOptions]
  )
  const datasetBounds = useMemo(() => (data ? responseBounds(data) : null), [data])

  const handleListSelect = useCallback(
    (entity: DetectionComparisonEntity) => {
      setSelectedId(entity.id)
      const bounds = entityBounds(entity, visibleDrives)
      if (bounds) setFocus(previous => ({ bounds, nonce: (previous?.nonce ?? 0) + 1 }))
    },
    [visibleDrives]
  )

  const handleDrivesChange = useCallback(
    (first: number | undefined, second: number | undefined) => {
      setSelectedId(null)
      setHiddenDrives(new Set())
      onDrivesChange(first, second)
    },
    [onDrivesChange]
  )

  const exportColumns = useMemo<TableColumn<ExportRow>[]>(
    () =>
      EXPORT_COLUMN_KEYS.map(([key, labelKey]) => ({
        key,
        label: t(`export.columns.${labelKey}` as 'export.columns.category'),
        visible: true,
      })),
    [t]
  )
  const exportRows = useMemo(
    () => (firstDriveId === undefined ? [] : buildExportRows(filtered, firstDriveId)),
    [filtered, firstDriveId]
  )
  const { downloadCsv } = useCsvDownload({
    data: exportRows,
    columns: exportColumns,
    fileName: `detection-comparison-${firstDriveId}-${secondDriveId}.csv`,
  })

  const summary = data
    ? [
        t('summary.detections', { count: data.detection_count }),
        t('summary.matchedPairs', { count: data.counts.matched }),
        data.hidden_count > 0 ? t('summary.hidden', { count: data.hidden_count }) : null,
        data.suppressed_count > 0
          ? t('summary.suppressed', { count: data.suppressed_count, radius: data.seen_once_suppression_radius_m })
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null

  return (
    <PageShell
      title={t('title')}
      fullWidth
      action={
        <Button leftSection={<TbDownload size={16} />} variant="light" disabled={!data} onClick={downloadCsv}>
          {t('export.button')}
        </Button>
      }
    >
      {summary && (
        <Text size="sm" c="dimmed" mb="sm">
          {summary}
        </Text>
      )}
      <Flex
        h={PAGE_HEIGHT}
        mih={480}
        style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-md)' }}
      >
        <Box
          w={SIDEBAR_WIDTH}
          style={{ flex: `0 0 ${SIDEBAR_WIDTH}px`, borderRight: '1px solid var(--mantine-color-default-border)' }}
        >
          <ScrollArea h="100%" type="auto">
            <Stack gap="md" p="md" mih="100%">
              <DriveSelectors firstDriveId={firstDriveId} secondDriveId={secondDriveId} onChange={handleDrivesChange} />
              {hasPair && (
                <>
                  <Divider />
                  <MatchingParamsControls params={params} onChange={setParams} />
                  <Divider />
                  <DisplayFilters
                    firstDriveId={firstDriveId}
                    secondDriveId={secondDriveId}
                    categoryCounts={categoryCounts}
                    driveCounts={driveCounts}
                    hiddenCategories={hiddenCategories}
                    hiddenDrives={hiddenDrives}
                    showPairLines={showPairLines}
                    showRejectedLinks={showRejectedLinks}
                    onToggleCategory={(key, visible) => setHiddenCategories(toggle(hiddenCategories, key, !visible))}
                    onToggleDrive={(driveId, visible) => setHiddenDrives(toggle(hiddenDrives, driveId, !visible))}
                    onShowPairLinesChange={setShowPairLines}
                    onShowRejectedLinksChange={setShowRejectedLinks}
                  />
                  <Divider />
                  <EntityList
                    entities={filtered}
                    firstDriveId={firstDriveId}
                    visibleDrives={visibleDrives}
                    selectedId={selectedId}
                    search={search}
                    onSearchChange={setSearch}
                    onSelect={handleListSelect}
                  />
                </>
              )}
            </Stack>
          </ScrollArea>
        </Box>

        <Box style={{ flex: 1, position: 'relative', minWidth: 0 }}>
          {!hasPair ? (
            <Center h="100%">
              <Stack gap={4} align="center">
                <Text fw={600}>{t('emptyState.title')}</Text>
                <Text size="sm" c="dimmed">
                  {t('emptyState.message')}
                </Text>
              </Stack>
            </Center>
          ) : (
            <>
              <LoadingOverlay visible={comparison.isFetching} zIndex={5} />
              {comparison.isError && (
                <Alert color="red" icon={<TbAlertTriangle />} m="md" style={overlayAlertStyle}>
                  {errorMessage(comparison.error, t('unableToLoad'))}
                </Alert>
              )}
              {data && data.detection_count === 0 && (
                <Alert color="gray" variant="light" m="md" style={overlayAlertStyle}>
                  {t('noDetections')}
                </Alert>
              )}
              <DetectionComparisonMap
                layers={layers}
                selection={selectionLayers}
                datasetBounds={datasetBounds}
                fitKey={`${firstDriveId}-${secondDriveId}`}
                focus={focus}
                onSelect={setSelectedId}
              />
              {selectedEntity && (
                <Box style={{ position: 'absolute', top: 12, right: 12, bottom: 12, zIndex: 4, display: 'flex' }}>
                  <EntityDetail
                    entity={selectedEntity}
                    firstDriveId={firstDriveId}
                    secondDriveId={secondDriveId}
                    onClose={() => setSelectedId(null)}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Flex>
    </PageShell>
  )
}
